/* SPDX-FileCopyrightText: Copyright (c) 2026 NVIDIA CORPORATION & AFFILIATES. All rights reserved. */
/* SPDX-License-Identifier: Apache-2.0 */

:host {
  --background-color: var(--nve-sys-interaction-background);
  --ring-color: var(--nve-sys-accent-secondary-background);
  --ring-background-opacity: 1;
  --ring-width: 2px;
  --width: var(--nve-ref-size-800);
  --height: var(--nve-ref-size-800);
  --animation-duration: calc(var(--nve-ref-animation-duration-400) * 2.5);

  width: var(--width);
  height: var(--height);
  container-type: size;
}

:host([size='xxs']) {
  --ring-width: 3px;
  --width: var(--nve-ref-size-300);
  --height: var(--nve-ref-size-300);
}

:host([size='xs']) {
  --ring-width: 3px;
  --width: var(--nve-ref-size-400);
  --height: var(--nve-ref-size-400);
}

:host([size='sm']) {
  --ring-width: 2.5px;
  --width: var(--nve-ref-size-600);
  --height: var(--nve-ref-size-600);
}

:host([size='lg']) {
  --width: var(--nve-ref-size-1000);
  --height: var(--nve-ref-size-1000);
}

:host([size='xl']) {
  --ring-width: 1px;
  --width: calc(2 * var(--nve-ref-space-xxxl));
  --height: calc(2 * var(--nve-ref-space-xxxl));
}

[internal-host] {
  display: flex;
  place-items: center;
  place-content: center;
  height: 100%;
}

svg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

circle.background {
  fill: var(--background-color);
  opacity: var(--ring-background-opacity);
  stroke: none;
}

circle.progress-ring-circle {
  fill: none;
  stroke: var(--ring-color);
  stroke-width: var(--ring-width);
  stroke-linecap: round;
  transform-origin: 50% 50%;
  transform: rotate(-90deg);
  transition: all 0.2s ease-in-out 0s;
}

:host([status='success']) {
  --ring-color: var(--nve-sys-support-success-emphasis-color);
  --background-color: var(--nve-sys-support-success-muted-color);
}

:host([status='warning']) {
  --ring-color: var(--nve-sys-support-warning-emphasis-color);
  --background-color: var(--nve-sys-support-warning-muted-color);
}

:host([status='danger']) {
  --ring-color: var(--nve-sys-support-danger-emphasis-color);
  --background-color: var(--nve-sys-support-danger-muted-color);
}

:host([status='neutral']) {
  --ring-background-opacity: 0.5;
  --ring-color: var(--color, var(--nve-sys-interaction-color));
  --background-color: var(--color, var(--nve-sys-interaction-color));
}

:host([status='accent']) [internal-host]:not([indeterminate]) circle.background {
  stroke: var(--background-color);
  fill: none;
  stroke-width: var(--ring-width);
}

:host([status='accent']) [internal-host][indeterminate] circle.background {
  display: none;
}

:host([status='neutral']) [internal-host]:not([indeterminate]) circle.background {
  stroke: var(--background-color);
  fill: none;
  stroke-width: var(--ring-width);
}

:host([status='neutral']) [internal-host][indeterminate] circle.background {
  display: none;
}

[internal-host][indeterminate] circle.progress-ring-circle {
  animation: var(--animation-duration) linear 0s infinite normal none running spin-infinite;
}

::slotted(nve-icon),
nve-icon {
  --width: 50cqw;
  --height: 50cqh;
  --color: var(--ring-color);
}

@keyframes spin-infinite {
  0% {
    stroke-dasharray: 0.01px, 43.97px;
    transform: rotate(0deg);
  }

  50% {
    stroke-dasharray: 21.99px, 21.99px;
    transform: rotate(450deg);
  }

  100% {
    stroke-dasharray: 0.01px, 43.97px;
    transform: rotate(1080deg);
  }
}
